import styled from '@emotion/styled';
import { Typography } from '../typography';

export const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  transition: max-height 0.3s ease;
  position: relative;
  z-index: 0;

  /* Calendar is position:absolute and overflows below this row; without a raised stacking context,
     the next filter section (sibling in the drawer) paints on top — broken overlap / “transparent” mix on CDRs & Campaigns. */
  &:has(.react-date-picker--open),
  &:has(.react-datetime-picker--open) {
    z-index: 5000;
    isolation: isolate;
  }

  // styles fop date picker
  .react-date-picker {
    flex-grow: 1;
    width: 100%;
  }

  .react-date-picker__wrapper {
    border: none;
  }

  .react-date-picker__inputGroup__input,
  .react-date-picker__inputGroup__divider,
  .react-date-picker__inputGroup__leadingZero {
    box-sizing: content-box;
    font-family: General Sans, sans-serif;
    font-size: 16px;
    color: ${({ theme }) => theme.colors.technical.black};

    &::selection {
      background-color: ${({ theme }) => theme.colors.secondary.medium};
    }

    &:invalid {
      background-color: ${({ theme }) => theme.colors.status.error.highlight};
    }
  }

  .react-date-picker__inputGroup__input:focus-visible {
    outline: none;
  }

  .react-date-picker__calendar-button {
    color: ${({ theme }) => theme.colors.technical.gray[60]};
    padding: 0;
  }

  .react-date-picker__calendar {
    margin: 14px -17px;
    z-index: 10000;
  }

  .react-calendar {
    max-width: calc(100% + 24px);
    box-shadow: 0 0 1px 0 rgba(0, 0, 0, 0.16), 0 2px 6px 0 rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    background-color: ${({ theme }) => theme.colors.technical.white};
    padding: 16px;
    border: 0;
    font-family: General Sans, sans-serif;
  }

  .react-calendar__month-view__weekdays__weekday {
    color: #000;
    font-size: 13px;
    font-weight: 400;
    line-height: 16px; /* 123.077% */
    text-transform: none;

    > abbr {
      text-decoration: none;
    }
  }

  .react-calendar__tile {
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px; /* 123.077% */
    color: ${({ theme }) => theme.colors.technical.black};
    border-radius: 8px;
    transition: color 0.2s ease, background-color 0.2s ease;
    will-change: color, background-color;

    &--now {
      background-color: transparent;
      font-weight: 600;
      color: ${({ theme }) => theme.colors.primary.normal};
    }

    &:not(.react-calendar__tile--active) {
      &:enabled:hover,
      &:enabled:focus {
        background-color: ${({ theme }) => theme.colors.technical.gray[5]};
      }
    }

    &--active,
    &--hasActive {
      background-color: ${({ theme }) => theme.colors.secondary.normal} !important;
      color: ${({ theme }) => theme.colors.primary.normal} !important;
      font-weight: 600;

      &:enabled:hover,
      &:enabled:focus {
        background-color: ${({ theme }) => theme.colors.secondary.normal} !important;
      }
    }

    &:disabled {
      background-color: transparent;
      color: ${({ theme }) => theme.colors.technical.gray[5]};
      cursor: not-allowed;
    }
  }

  .react-calendar__month-view__days__day--neighboringMonth {
    color: ${({ theme }) => theme.colors.technical.gray[15]};
  }

  .react-calendar__navigation__prev2-button,
  .react-calendar__navigation__next2-button {
    display: none;
  }

  .react-calendar__navigation {
    height: auto;
    margin-bottom: 8px;
  }

  .react-calendar__navigation__label {
    order: -1;
    min-width: unset;
    text-align: left;
    padding: 0;

    &:enabled:hover {
      background-color: transparent;
      color: ${({ theme }) => theme.colors.primary.normal};

      .react-calendar__navigation__label__labelText {
        color: inherit;
      }
    }
  }

  .react-calendar__navigation__label__labelText {
    font-size: 13px;
    font-style: normal;
    font-weight: 600;
    line-height: 16px; /* 123.077% */
    color: ${({ theme }) => theme.colors.technical.gray[60]};
    transition: color 0.2s ease;
    will-change: color;
  }

  .react-calendar__navigation button {
    min-width: unset;
    background: none;
    transition: color 0.2s ease;
    will-change: color;
    padding: 0 4px;

    &:disabled {
      background-color: transparent;
      color: ${({ theme }) => theme.colors.technical.gray[15]};
      cursor: not-allowed;
    }

    &:enabled:hover,
    &:enabled:focus {
      background-color: transparent;
      color: ${({ theme }) => theme.colors.primary.normal};
    }
  }

  .react-calendar__year-view .react-calendar__tile,
  .react-calendar__decade-view .react-calendar__tile,
  .react-calendar__century-view .react-calendar__tile {
    padding-top: 8px;
    padding-bottom: 8px;
    border-radius: 12px;
    flex-basis: calc(100% / 3 - 6px) !important;

    &:enabled {
      background-color: ${({ theme }) => theme.colors.technical.gray[5]};

      &:hover,
      &:focus {
        background-color: ${({ theme }) => theme.colors.technical.gray[15]};
      }
    }
  }

  .react-calendar__decade-view__years,
  .react-calendar__century-view__decades,
  .react-calendar__year-view__months {
    gap: 8px;
  }


// Styles for date-time picker
  .react-datetime-picker {
    flex-grow: 1;
    width: 100%;
    min-width: 0;
  }

  &:has(.react-datetime-picker--open),
  &:has(.react-datetime-picker--open) .react-datetime-picker__clock {
    z-index: 10000;
  }

  .react-datetime-picker__clock {
    position: relative;
    z-index: 10001;
    pointer-events: auto;
  }

  .react-datetime-picker__clock *,
  .react-datetime-picker__clock-button {
    pointer-events: auto;
  }

  .react-clock {
    pointer-events: auto;
    touch-action: manipulation;
  }

  .react-datetime-picker__wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    min-width: 0;
    border: none;
  }

  .react-datetime-picker__inputGroup {
    flex-grow: 1;
    min-width: 0;
    padding: 0;
    font-family: General Sans, sans-serif;
    font-size: 16px;
    color: ${({ theme }) => theme.colors.technical.black};
  }

  .react-datetime-picker__inputGroup__input,
  .react-datetime-picker__inputGroup__divider,
  .react-datetime-picker__inputGroup__leadingZero {
    box-sizing: content-box;
    font-family: General Sans, sans-serif;
    font-size: 16px;
    color: ${({ theme }) => theme.colors.technical.black};

    &::selection {
      background-color: ${({ theme }) => theme.colors.secondary.medium};
    }

    &:invalid {
      background-color: ${({ theme }) => theme.colors.status.error.highlight};
    }
  }

  .react-datetime-picker__inputGroup__input:focus-visible {
    outline: none;
  }
`;

export const Label = styled.label`
  display: flex;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.technical.adaptive};
  align-items: center;
`;

export const LabelHint = styled(Typography)`
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  margin-left: auto;
`;

export const InputContainer = styled.div<{
  hasError?: boolean;
  hasHint?: boolean;
  isFocused?: boolean;
  variant?: 'default' | 'subtle';
  disabled?: boolean;
  isTtlField?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${({ isTtlField }) => (isTtlField ? `0 8px` : `0 16px`)};;
  height: 48px;
  border-radius: 12px 12px 12px 0;
  border: 1px solid;
  color: ${({ theme }) => theme.colors.technical.gray[30]};
  border-color: ${({ theme, hasError, hasHint, isFocused, variant }) => {
    if (hasHint) return theme.colors.status.warning.normal;
    if (hasError) return theme.colors.status.error.normal;
    if (isFocused) return theme.colors.primary.normal;
    if (variant === 'subtle') return 'transparent';
    return theme.colors.technical.gray[15];
  }};
  background-color: ${({ theme, disabled }) =>
    disabled ? theme.colors.background.medium : theme.colors.technical.white};
  transition: all 0.2s ease;

  ${({ disabled }) =>
    disabled &&
    `
    cursor: not-allowed;
    pointer-events: none;
  `}

  ${({ isTtlField }) =>
    isTtlField &&
    `
    justify-content: center;
    border-radius: 12px;
    width: 64px;
    min-width: 64px;
    flex-shrink: 0;
  `}

  &:hover {
    border-color: ${({ theme, hasError, hasHint, variant, disabled }) => {
      if (disabled) return theme.colors.technical.gray[15];
      if (hasHint) return theme.colors.status.warning.normal;
      if (hasError) return theme.colors.status.error.normal;
      if (variant === 'subtle') return theme.colors.technical.gray[15];
      return theme.colors.technical.gray[30];
    }};
  }

  &:focus-within {
    border-color: ${({ theme, hasError, hasHint, disabled }) => {
      if (disabled) return theme.colors.technical.gray[15];
      if (hasHint) return theme.colors.status.warning.normal;
      if (hasError) return theme.colors.status.error.normal;
      return theme.colors.primary.normal;
    }};
    box-shadow: ${({ theme, hasError, hasHint, disabled }) => {
      if (disabled) return 'none';
      if (hasHint) return `0px 0px 0px 4px ${theme.colors.status.warning.highlight}`;
      if (hasError) return `0px 0px 0px 4px ${theme.colors.status.error.highlight}`;
      return `0px 0px 0px 4px ${theme.colors.secondary.normal}`;
    }};
  }
`;

export const Input = styled.input<{ disabled?: boolean, isTtlField?: boolean }>`
  width: ${({ isTtlField }) => (isTtlField ? `64px` : `100%`)};
  border: none;
  background: none;
  font-size: 16px;
  color: ${({ theme, disabled }) => (disabled ? theme.colors.technical.gray[15] : theme.colors.technical.black)};
  padding: 0;
  outline: none;
  text-align: ${({ isTtlField }) => (isTtlField ? 'center' : 'inherit')};

  &::placeholder {
    color: ${({ theme }) => theme.colors.technical.gray[15]};
  }

  &:disabled {
    pointer-events: none;
    user-select: none;
  }
`;

export const LabelRow = styled(Label)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

export const LabelText = styled(Label)`
  margin-bottom: 0;
`;

export const CharacterCount = styled.span`
  font-size: 10px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.technical.gray[60]};
`;
